import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalContext } from '~/context/GlobalContext';
import { useDeviceType } from '~/hooks/useDeviceType';
import { authStorage } from '~/services/authStorage';
import { stopTokenRefresh } from '~/services/apiService';
import { IChatRoom, IChatUser, IData, IDataInvite, IRoomProps, IRouletteFilters, IUser, IUserStorage, IDataRoomsCount, IWebRTCData, IMessage, IModeration, IBanWarning } from '~/types/common';
import stylesMessage from './MessageContainer/Message.module.css';

declare global {
  interface Window {
    chatDebug: () => void;
    setChatContext: React.Dispatch<React.SetStateAction<any>>;
  }
}

function Websocket() {
  const websocketRef = useRef<WebSocket | null>(null);
  const { context, setContext } = useGlobalContext();
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const pingIntervalRef = useRef<NodeJS.Timer | null>(null);
  const deviceType = useDeviceType();

  // Tryb sesji backendowej (chat lub roulette). Utrzymujemy w refie, żeby przeżył remount.
  const modeRef = useRef<'chat' | 'roulette' | null>(null);
  // Czy socket został już zainicjowany (one-shot guard).
  const hasConnectedRef = useRef(false);
  // Najświeższy context — do odczytu w transition handlerze bez wymuszania re-runów efektu.
  const contextRef = useRef(context);
  contextRef.current = context;
  // Najświeższy location.state — używane przy transition do odczytu room/name/gender.
  const stateRef = useRef<any>(state);
  stateRef.current = state;

  const join = (data: IData) => {
    if (!data) return;
    const { event, user, room, message } = data;

    if (!room || !user || !message) return;

    const userStorage: IUserStorage = {
      id: user.id,
      name: user.name,
      gender: user.gender,
      deviceType: user.deviceType,
      time: user.time,
      rooms: user.rooms,
      avatar: (user as any).avatar ?? null,
    }

    window.sessionStorage.setItem('user', JSON.stringify(userStorage));

    setContext(prev => {
      // 1. Sprawdź, czy pokój już istnieje w stanie
      const roomExists = prev.rooms.some(r => r.id === room.id);

      // 2. Przygotuj nową tablicę pokoi
      let updatedRooms;

      if (roomExists) {
        // 2a. Jeśli pokój istnieje, dodaj nowe wiadomości z room.messages
        updatedRooms = prev.rooms.map(r => {
          if (r.id === room.id) {
            const newMessages = r.messages
              ? [...r.messages, ...(room.messages || [])] // dodajemy nowe wiadomości z room
              : [...(room.messages || [])];              // jeśli brak wcześniejszych wiadomości, bierzemy tylko room.messages
            return { ...r, messages: newMessages };
          }
          return r; // inne pokoje bez zmian
        });
      } else {
        // 2b. Jeśli pokoju nie ma, dodajemy go jako nowy wraz z wiadomościami z room
        updatedRooms = [...prev.rooms, { ...room, messages: [...(room.messages || [])] }];
      }

      // 3. Zwracamy nowy stan kontekstu
      return {
        ...prev,
        activeSidebarId: '',          // ustawiamy zakładki sidebar na nieaktywne
        activeRoomId: room.id,        // ustawiamy nowo aktywny pokój
        room: room,                   // zapisujemy obecny pokój
        rooms: updatedRooms,          // aktualizujemy tablicę pokoi
        user: user,                   // aktualizujemy informacje o użytkowniku
        privateWaitingPopupRooms: null // reset oczekujących na dołączenie
      };
    });

    window.dispatchEvent(new CustomEvent('audioNotification', {
      bubbles: true,
      detail: data
    }));

    ping();
  };

  // Powrót z ruletki do czatu — backend przysyła pełny snapshot pokoi usera (z wiadomościami,
  // które przyszły w trakcie ruletki). Nadpisujemy `rooms` i `room`, resetujemy stan ruletki.
  const restoreChat = (data: any) => {
    if (!data?.user) return;
    const { user, rooms, room } = data;

    const userStorage: IUserStorage = {
      id: user.id,
      name: user.name,
      gender: user.gender,
      deviceType: user.deviceType,
      time: user.time,
      rooms: user.rooms,
      avatar: user.avatar ?? null,
    };
    window.sessionStorage.setItem('user', JSON.stringify(userStorage));

    setContext(prev => ({
      ...prev,
      user,
      rooms: Array.isArray(rooms) ? rooms : [],
      room: room || null,
      activeSidebarId: '',
      callSession: null,
      localStream: null,
      remoteStream: null,
      roulette: {
        ...prev.roulette,
        status: 'idle',
        partner: null,
        roomId: null,
        isInitiator: false,
      },
    }));

    ping();
  };

  const privateAccept = (data: IData & { pendingRoomId?: string }) => {
    if (!data) return;

    const { room: newRoom, message } = data; // <-- dodaj wiadomość z serwera
    if (!newRoom) return;

    const welcomeMessage = message ? [message] : []; // jeśli message jest null, to pusty array

    setContext(prev => {
      let updatedRooms = [...prev.rooms];

      if (data.pendingRoomId) {
        updatedRooms = updatedRooms.filter(r => r.id !== data.pendingRoomId);
      }

      if (newRoom.private && newRoom.users && newRoom.users.length === 2) {
        const targetUserId = newRoom.users.find(u => u.id !== prev.user?.id)?.id;
        if (targetUserId) {
          updatedRooms = updatedRooms.filter(r => {
            if (!r.private || !r.users) return true;
            return !r.users.some(u => u.id === targetUserId);
          });
        }
      }

      updatedRooms.push({ ...newRoom, messages: [...welcomeMessage] });

      return {
        ...prev,
        rooms: updatedRooms,
        activeRoomId: newRoom.id,
        room: { ...newRoom, messages: [...welcomeMessage] },
        privateWaitingPopupRooms: null,
      };
    });

    window.dispatchEvent(new CustomEvent('audioNotification', {
      bubbles: true,
      detail: data
    }));
  };

  // Synchronizacja prywatnego czatu na kolejne urządzenie tego samego konta (jak w Messengerze):
  // dodajemy pokój do listy bez przełączania aktywnego widoku i bez dźwięku.
  const privateRoomSync = (data: { room?: IChatRoom }) => {
    const newRoom = data?.room;
    if (!newRoom?.id) return;

    setContext(prev => {
      const existing = prev.rooms.find(r => r.id === newRoom.id);
      let updatedRooms;
      if (existing) {
        // Pokój już znany — uzupełnij ewentualnie brakujące wiadomości/skład bez nadpisywania licznika.
        updatedRooms = prev.rooms.map(r =>
          r.id === newRoom.id
            ? { ...r, ...newRoom, messages: r.messages?.length ? r.messages : (newRoom.messages || []) }
            : r
        );
      } else {
        updatedRooms = [...prev.rooms, { ...newRoom, messages: [...(newRoom.messages || [])] }];
      }
      return { ...prev, rooms: updatedRooms };
    });
  };

  const privateInvite = (data: IDataInvite) => {
    if (!data) return;

    const { fromUser, inviteId, time } = data;

    if (inviteId && fromUser) {
      setContext(prev => ({
        ...prev,
        privateWaitingUsers: [
          ...(prev.privateWaitingUsers || []),
          {
            inviteId: inviteId,
            fromUser: fromUser,
            time: time
          }
        ]
      }));

      window.dispatchEvent(new CustomEvent('audioNotificationInvite', {
        bubbles: true,
        detail: data
      }));
    }
  };

  const privateCancel = (data: IDataInvite) => {
    if (!data) return;

    const { inviteId, message: serverTextMessage } = data;

    setContext((prev: any) => {
      // 1. Czyścimy popupy zaproszeń
      const updatedWaitingUsers = (prev.privateWaitingUsers || []).filter(
        (u: any) => u.inviteId !== inviteId
      );

      // 2. Aktualizujemy listę pokoi (żeby po kliknięciu w sidebarze wiadomość tam była)
      const updatedRooms = prev.rooms.map((r: any) => {
        if (r.id === inviteId) {
          return {
            ...r,
            messages: [...(r.messages || []), data.message]
          };
        }
        return r;
      });

      // 3. KLUCZOWE: Aktualizujemy aktywny widok, jeśli user na niego patrzy
      const updatedActiveRoom = prev.room?.id === inviteId
        ? { ...prev.room, messages: [...(prev.room.messages || []), data.message] }
        : prev.room;

      return {
        ...prev,
        privateWaitingUsers: updatedWaitingUsers,
        rooms: updatedRooms,
        room: updatedActiveRoom,
      };
    });

    window.dispatchEvent(new CustomEvent('audioNotification', {
      bubbles: true,
      detail: data
    }));
  };

  const message = (data: IData) => {
    const { room, message } = data;
    if (!data || !room || !message) return;

    setContext(prev => {
      const isRoomActive = prev.room?.id === room.id && prev.activeSidebarId === '';
      const updatedRooms = prev.rooms.map(r => {
        if (r.id === room.id) {
          return {
            ...r,
            messages: [...(r.messages || []), message],
            // Jeśli pokój nie jest aktywny, zwiększ licznik nieprzeczytanych
            unreadCount: isRoomActive ? 0 : (r.unreadCount || 0) + 1
          };
        }
        return r;
      });

      return {
        ...prev,
        rooms: updatedRooms,
        // Jeśli pokój jest aktywny, aktualizujemy też obiekt 'room'
        room: isRoomActive ? {
          ...prev.room!,
          messages: [...(prev.room!.messages || []), message],
          unreadCount: 0
        } : prev.room
      };
    });

    if (data.audioNotification) {
      window.dispatchEvent(new CustomEvent('audioNotification', {
        bubbles: true,
        detail: data
      }));
    }
  };

  const updateReaction = (data: { event: string; roomId: string; messageId: string; reactions: { [k: string]: string[] } }) => {
    const { roomId, messageId, reactions } = data;
    setContext(prev => {
      const updatedRooms = prev.rooms.map(r => {
        if (r.id !== roomId) return r;
        return {
          ...r,
          messages: (r.messages || []).map(m => m.id === messageId ? { ...m, reactions } : m)
        };
      });
      const updatedRoom = prev.room?.id === roomId
        ? { ...prev.room!, messages: (prev.room!.messages || []).map(m => m.id === messageId ? { ...m, reactions } : m) }
        : prev.room;
      return { ...prev, rooms: updatedRooms, room: updatedRoom };
    });
  };

  const removeMessage = (data: IModeration) => {
    const element = document.getElementById(data?.messageId);
    if (element) {
      element.remove();
    }
  };

  const hideMessage = (data: IModeration) => {
    const { messageId, messageText } = data;
    const element = document.getElementById(messageId);

    if (element) {
      const messageContainer = element.querySelector(`.${stylesMessage.messageText}`);

      if (messageContainer) {
        messageContainer.innerHTML = `<span class="${stylesMessage.moderatedText}">${messageText}</span>`;
      }
    }
  };

  const close = (data: { event: string, message: string }, reason: string = 'notActive') => {
    window.sessionStorage.removeItem('user');

    if (reason === 'forceLogout' || reason === 'banned' || reason === 'kicked') {
      stopTokenRefresh();
      authStorage.clear();
      setContext({
        authUser: null,
        activeSidebarId: '',
        user: null,
        room: null,
        rooms: [],
        publicRooms: null,
        privateWaitingUsers: [],
        callSession: null,
        localStream: null,
        remoteStream: null,
        searchQuery: '',
      });
    } else {
      setContext({
        activeSidebarId: '',
        user: null,
        room: null,
        rooms: [],
        publicRooms: null,
      });
    }

    // zatrzymaj ping
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    // zamknij socket
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }

    hasConnectedRef.current = false;

    window.sessionStorage.setItem('logout_reason', reason);
    window.location.href = "/";
  };

  const getPeople = (data: IData) => {
    const { event, user, room } = data;
    setContext(prev => ({
      ...prev,
      room
    }));
  };

  const getPeopleCount = (data: { event: string; roomCounts: { room: string; count: number }[] }) => {
    // 1. Wyciągamy tablicę z danymi z backendu
    const { roomCounts } = data;

    if (!roomCounts || !Array.isArray(roomCounts)) return;

    // 2. Aktualizujemy kontekst
    setContext(prev => {
      // Jeśli publicRooms jest jeszcze puste (null), nie mamy czego mapować
      if (!prev.publicRooms) return {};

      // 3. Mapujemy aktualną listę publicRooms
      const updatedPublicRooms = prev.publicRooms.map((room) => {
        // Szukamy odpowiadającego rekordu w danych z backendu
        // Porównujemy room.id z s.room
        const stats = roomCounts.find(s => s.room === room.id);

        return {
          ...room,
          // Jeśli znaleziono statystyki, ustawiamy nową liczbę, w przeciwnym razie 0
          usersCount: stats ? stats.count : 0
        };
      });

      // 4. Zwracamy tylko zmienioną część kontekstu
      return {
        publicRooms: updatedPublicRooms
      };
    });
  };

  // Interwał pingu aktywności (ms). Próg nieaktywności na serwerze (USER_ACTIVE_MS)
  // wynosi 30 min, więc ping co 5 min daje 6 prób w oknie — utrata nawet ~5 pingów
  // z rzędu nie wyrzuci aktywnego usera (odporność na zgubione/spóźnione pingi),
  // a zarazem redukuje ruch i obciążenie serwera 300× względem poprzedniego 1 s.
  const PING_INTERVAL_MS = 300000;

  const ping = () => {
    if (pingIntervalRef.current) return;

    pingIntervalRef.current = setInterval(() => {
      const storedData = window.sessionStorage.getItem('user');
      const user = storedData ? JSON.parse(storedData) : null;
      if (!user) return;

      websocketRef.current?.send(
        JSON.stringify({
          event: 'isActive',
          user: { ...user, event: 'isActive' },
          room: null,
        })
      );
    }, PING_INTERVAL_MS);
  };

  // Inne urządzenie tego konta przejęło rozmowę/ruletkę → to urządzenie przerywa
  // dzwonienie/oczekiwanie, gasi WebRTC i wraca do czatu ogólnego.
  const forceReturnToGeneralChat = () => {
    // 1. Wycisz oba (niezależne) dźwięki dzwonienia.
    window.dispatchEvent(new CustomEvent('stopCallingSound', { detail: false }));
    window.dispatchEvent(new CustomEvent('stopIncomingCallSound', { detail: false }));
    // 2. Twardy teardown mediów (kamera/mikrofon/peer) na zamontowanym useWebRTC.
    window.dispatchEvent(new CustomEvent('forceCleanupWebRTC'));
    // 3. Zdejmij nakładki połączenia w VideoContainer (imperatywny DOM) — bez emisji do serwera.
    window.dispatchEvent(new CustomEvent('cancelCall', {
      detail: { event: 'cancelCall', initiatorId: '', partnerId: '', roomId: '', session: null },
    }));
    // 4. Reset stanu połączenia/ruletki.
    setContext(prev => ({
      ...prev,
      callSession: null,
      localStream: null,
      remoteStream: null,
      roulette: { ...prev.roulette, status: 'idle', partner: null, roomId: null, isInitiator: false },
    }));
    // 5. Powrót do czatu ogólnego.
    const ctx = contextRef.current;
    const generalId = ctx.publicRooms?.find((r: any) => r.label === 'general')?.id
      || ctx.publicRooms?.[0]?.id
      || '1';
    // window.location.pathname jest pewniejszy niż domknięty location (onmessage powstaje raz).
    if (window.location.pathname === '/roulette') {
      navigate('/chat', {
        state: {
          room: generalId,
          name: ctx.user?.name || 'guest',
          gender: ctx.user?.gender || 'male',
        },
      });
    } else {
      // Już na /chat — nawigacja tą samą ścieżką jest no-op, więc używamy changeRoom.
      window.dispatchEvent(new CustomEvent('changeRoom', {
        detail: { id: generalId, name: '', public: true, private: false, label: 'general', region: '', users: null },
      }));
    }
  };

  const closeRoom = (data: { room: IChatRoom; user: IChatUser }) => {
    const { room } = data;
    if (!room) return;

    setContext(prev => {
      const wasActive = prev.room?.id === room.id;
      const closedIndex = prev.rooms.findIndex(r => r.id === room.id);
      const updatedRooms = prev.rooms.filter(r => r.id !== room.id);

      // Zamknięcie pokoju, którego nie oglądamy (np. czat prywatny zamknięty na innym urządzeniu konta)
      // — tylko usuwamy go z listy, nie zmieniamy aktywnego widoku.
      if (!wasActive) {
        return { ...prev, rooms: updatedRooms };
      }

      let nextRoom: IChatRoom | null = null;
      if (updatedRooms.length > 0) {
        // Jeśli zamknęliśmy aktywny pokój, wybieramy sąsiedni
        nextRoom = updatedRooms[closedIndex] || updatedRooms[closedIndex - 1] || updatedRooms[0];
      }

      return {
        ...prev,
        rooms: updatedRooms,
        room: nextRoom,
        activeRoomId: nextRoom ? nextRoom.id : '',
      };
    });
  };

  useEffect(() => {
    // Wykryj, w którym trybie powinniśmy działać na podstawie obecnej trasy.
    const desiredMode: 'chat' | 'roulette' | null =
      location.pathname === '/chat' ? 'chat'
      : location.pathname === '/roulette' ? 'roulette'
      : null;

    // Trasy spoza /chat i /roulette — nie ruszamy socketu (nawigacja w obrębie sesji).
    if (!desiredMode) return;

    // SOCKET JUŻ ŻYJE — wystarczy zmiana trybu, bez zrywania połączenia.
    if (hasConnectedRef.current && websocketRef.current) {
      if (modeRef.current === desiredMode) return; // ta sama trasa, no-op
      const ws = websocketRef.current;
      const sendSwitch = () => {
        if (desiredMode === 'roulette') {
          ws.send(JSON.stringify({
            event: 'switchMode',
            mode: 'roulette',
            filters: contextRef.current.roulette?.filters || { roomFilter: 'any', genderFilter: 'any' },
          }));
        } else {
          const targetRoom = stateRef.current?.room
            || contextRef.current.publicRooms?.[0]?.id
            || '1';
          ws.send(JSON.stringify({ event: 'switchMode', mode: 'chat', targetRoom }));
        }
      };
      if (ws.readyState === WebSocket.OPEN) sendSwitch();
      else ws.addEventListener('open', sendSwitch, { once: true });
      modeRef.current = desiredMode;
      return;
    }

    // Walidacja danych wejściowych dla PIERWSZEGO połączenia.
    if (desiredMode === 'chat' && !state?.room) return;

    hasConnectedRef.current = true;
    modeRef.current = desiredMode;

    const socket = new WebSocket(process.env.REACT_APP_WEBSOCKET_SERVER as string);
    websocketRef.current = socket;

    socket.onopen = () => {
      console.log('Połączono z serwerem WebSocket');

      // Token JWT (jeśli zalogowany) — backend wiąże to połączenie z kontem (multi-device).
      // Goście wysyłają null i zachowują dotychczasowe zachowanie single-device.
      const authToken = authStorage.getAccessToken();

      if (desiredMode === 'roulette') {
        // W trybie ruletki nie ma statycznego pokoju — backend zapisuje filtry i wrzuca do kolejki.
        socket.send(JSON.stringify({
          event: 'rouletteJoin',
          token: authToken,
          user: {
            name: state?.name || 'guest',
            gender: state?.gender || '',
            deviceType,
          },
          filters: { roomFilter: 'any', genderFilter: 'any' },
        }));
        return;
      }

      const data: IData = {
        event: 'join',
        audioNotification: false,
        user: {
          id: '',
          name: state.name ? state.name : null,
          gender: state.gender ? state.gender : '',
          deviceType: deviceType,
          time: '',
          rooms: [],
          ws: null,
        },
        room: {
          id: state.room,
          name: '',
          public: false,
          private: false,
          label: '',
          region: '',
          users: null,
        },
      };
      socket.send(JSON.stringify({ ...data, token: authToken }));
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      switch (data.event) {
        case 'join':
          join(data);
          break;
        case 'restoreChat':
          restoreChat(data);
          break;
        case 'privateAccept':
          privateAccept(data);
          break;
        case 'privateInvite':
          privateInvite(data);
          break;
        case 'privateInviteSent':
          break;
        case 'privateRoomPending':
          join(data);
          break;
        case 'privateRoomSync':
          privateRoomSync(data);
          break;
        case 'privateCancel':
          privateCancel(data);
          break;
        case 'closeRoom':
          closeRoom(data);
          break;
        case 'changeRoom':
          join(data);
          break;
        case 'serverMessage':
          message(data);
          break;
        case 'updateReaction':
          updateReaction(data as any);
          break;
        case 'deleteMessage':
          removeMessage(data);
          break;
        case 'hideMessage':
          hideMessage(data);
          break;
        case 'banWarning':
          window.dispatchEvent(new CustomEvent('banWarningReceived', { detail: data as IBanWarning }));
          break;
        case 'getPeople':
          getPeople(data);
          break;
        case 'userAvatarChanged':
          setContext(prev => {
            const changedUser = data.user as { id: string; name?: string; avatar?: string | null };
            if (!changedUser?.id) return prev;

            const remapUsers = (users: any[] | null | undefined) =>
              (users || []).map(u => u.id === changedUser.id ? { ...u, avatar: changedUser.avatar ?? null } : u);

            const updatedRooms = prev.rooms.map(r => ({
              ...r,
              users: remapUsers(r.users) as any,
              messages: (r.messages || []).map(m =>
                m.userId === changedUser.id ? { ...m, userAvatar: changedUser.avatar ?? null } : m,
              ),
            }));

            const updatedRoom = prev.room ? {
              ...prev.room,
              users: remapUsers(prev.room.users) as any,
              messages: (prev.room.messages || []).map(m =>
                m.userId === changedUser.id ? { ...m, userAvatar: changedUser.avatar ?? null } : m,
              ),
            } : prev.room;

            // Jeśli to my — zaktualizuj też sessionStorage user.avatar
            if (prev.user?.id === changedUser.id) {
              const storedData = window.sessionStorage.getItem('user');
              if (storedData) {
                try {
                  const stored = JSON.parse(storedData);
                  stored.avatar = changedUser.avatar ?? null;
                  window.sessionStorage.setItem('user', JSON.stringify(stored));
                } catch { /* ignoruj */ }
              }
            }

            return {
              ...prev,
              user: prev.user?.id === changedUser.id
                ? { ...prev.user, avatar: changedUser.avatar ?? null } as any
                : prev.user,
              rooms: updatedRooms,
              room: updatedRoom,
            };
          });
          break;
        case 'userStatusChanged':
          setContext(prev => {
            if (!prev.room) return prev;
            const { userId, status } = data as { userId: string; status: 'active' | 'inactive' };
            return {
              ...prev,
              room: {
                ...prev.room,
                users: (prev.room.users || []).map((u: any) =>
                  u.id === userId ? { ...u, status } : u
                ),
              },
            };
          });
          break;
        case 'getPeopleCount':
          getPeopleCount(data);
          break;
        case 'isActive':
          const userStorage: IUserStorage = {
            id: data.user.id,
            name: data.user.name,
            gender: data.user.gender,
            deviceType: data.user.deviceType,
            time: data.user.time,
            rooms: data.user.rooms,
            avatar: data.user.avatar ?? null,
          }
          window.sessionStorage.setItem('user', JSON.stringify(userStorage));
          break;
        case 'notActive':
          close(data);
          break;
        case 'forceLogout':
          close(data, 'forceLogout');
          break;
        case 'sessionTerminated':
          // Zdalne wylogowanie tego urządzenia z /profile na innym urządzeniu.
          // Najpierw gasimy kamerę (gdyby trwała rozmowa), potem pełne czyszczenie jak forceLogout.
          window.dispatchEvent(new CustomEvent('forceCleanupWebRTC'));
          close(data, 'forceLogout');
          break;
        case 'forceReturnToGeneralChat':
          forceReturnToGeneralChat();
          break;
        case 'kicked':
          close(data, 'kicked');
          break;
        case 'banned':
          close(data, 'banned');
          break;
        case 'userRemoved':
          message(data);
          break;
        case 'userCalling':
          window.dispatchEvent(new CustomEvent('userCalling', { detail: data }));
          break;
        case 'callSessionAccepted':
          window.dispatchEvent(new CustomEvent('callSessionAccepted', { detail: data }));
          break;
        case 'cancelCall':
          window.dispatchEvent(new CustomEvent('cancelCall', { detail: data }));
          break;
        case 'userIsBusy':
          window.dispatchEvent(new CustomEvent('userIsBusy', { detail: data }));
          break;
        case 'webRtcOffer':
          window.dispatchEvent(new CustomEvent('webRtcOffer', { detail: data }));
          break;
        case 'webRtcAnswer':
          window.dispatchEvent(new CustomEvent('webRtcAnswer', { detail: data }));
          break;
        case 'webRtcCandidate':
          window.dispatchEvent(new CustomEvent('webRtcCandidate', { detail: data }));
          break;
        case 'rouletteJoin':
          // Backend potwierdza zarejestrowanie sesji ruletki — zapisz usera w sessionStorage.
          if (data.user) {
            const userStorage: IUserStorage = {
              id: data.user.id,
              name: data.user.name,
              gender: data.user.gender,
              deviceType: data.user.deviceType,
              time: data.user.time,
              rooms: data.user.rooms || [],
              avatar: data.user.avatar ?? null,
            };
            window.sessionStorage.setItem('user', JSON.stringify(userStorage));
            setContext(prev => ({ ...prev, user: data.user as IUser }));
          }
          ping();
          break;
        case 'rouletteWaiting':
          setContext(prev => ({
            ...prev,
            roulette: { ...prev.roulette, status: 'waiting', partner: null, roomId: null, isInitiator: false },
            room: null,
            rooms: [],
            callSession: null,
          }));
          break;
        case 'rouletteMatched': {
          const { partner, room: rouletteRoom, isInitiator } = data as {
            partner: IUser, room: IChatRoom, isInitiator: boolean
          };
          setContext(prev => ({
            ...prev,
            roulette: {
              ...prev.roulette,
              status: 'matched',
              partner,
              roomId: rouletteRoom.id,
              isInitiator,
            },
            room: { ...rouletteRoom, messages: [], private: true },
            rooms: [{ ...rouletteRoom, messages: [], private: true }],
            callSession: {
              event: 'rouletteSession',
              initiatorId: isInitiator ? (prev.user?.id || '') : partner.id,
              initiatorName: isInitiator ? (prev.user?.name || '') : partner.name,
              partnerId: isInitiator ? partner.id : (prev.user?.id || ''),
              partnerName: isInitiator ? partner.name : (prev.user?.name || ''),
              roomId: rouletteRoom.id,
              isUserBusy: true,
            },
          }));
          window.dispatchEvent(new CustomEvent('rouletteMatched', { detail: data }));
          break;
        }
        case 'roulettePartnerLeft':
          setContext(prev => ({
            ...prev,
            roulette: { ...prev.roulette, status: 'waiting', partner: null, roomId: null, isInitiator: false },
            room: null,
            rooms: [],
            callSession: null,
            remoteStream: null,
          }));
          window.dispatchEvent(new CustomEvent('roulettePartnerLeft', { detail: data }));
          break;
        default:
          console.log("Event nie zarejestrowany: ", data);
          break;
      }
    };

    socket.onerror = () => {
      if (!window.sessionStorage.getItem('logout_reason')) {
        close({ event: 'notActive', message: 'Zostałeś rozłączony z serwerem.' });
      }
    };

    socket.onclose = () => {
      if (!window.sessionStorage.getItem('logout_reason')) {
        close({ event: 'notActive', message: 'Zostałeś rozłączony z serwerem.' });
      }
    };

    const handleUserMessage = (e: any) => {
      if (!e.detail) return;

      const { value, roomId } = e.detail as { type: string, value: string, roomId: string };

      const storedUser = window.sessionStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      if (!user) return;

      socket.send(JSON.stringify({
        event: 'userMessage',
        user: user,
        room: {
          id: roomId,
          name: '',
          public: false,
          private: false,
          label: '',
          region: '',
          users: null
        },
        message: {
          roomId: '',
          isMine: '',
          serverTime: '',
          serverText: '',
          userText: value,
          userId: '',
          userName: '',
        }
      }));
    };

    const handleChangeRoom = (e: any) => {
      if (!e.detail) return;
      const room = e.detail as IChatRoom;
      const storedUser = window.sessionStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;
      if (!user) return;

      const isInRoom = user.rooms?.find((item: any) => item.id === room.id);
      if (isInRoom) return;

      user.event = 'changeRoom';

      socket.send(JSON.stringify({
        event: 'changeRoom',
        user: user,
        room: room
      }));
    };

    const handleGetPeople = (e: any) => {
      if (!e.detail) return;
      const data: IData = {
        event: 'getPeople',
        audioNotification: false,
        user: {
          id: '',
          name: '',
          gender: '',
          deviceType: deviceType,
          time: '',
          rooms: [],
          ws: null
        },
        room: {
          id: e.detail.roomId,
          name: '',
          public: false,
          private: false,
          label: '',
          region: '',
          users: null
        },
      };
      socket.send(JSON.stringify(data));
    };

    const handleGetPeopleCount = (e: any) => {
      const storedUser = window.sessionStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;
      if (!user) return;

      const data: IData = {
        event: 'getPeopleCount',
        audioNotification: false,
        user: user,
        room: null
      };
      socket.send(JSON.stringify(data));
    };

    const handlePrivateJoin = (e: any) => {
      if (!e.detail) return;

      const { name, id } = e.detail as IChatUser;
      const storedData = window.sessionStorage.getItem('user');
      const data = storedData ? JSON.parse(storedData) : null;
      if (!data) return;

      socket.send(JSON.stringify({
        event: 'privateJoin',
        user: {
          ...data,
          event: 'privateJoin',
        },
        findUserById: id
      }));
    };

    const handlePrivateAccept = (e: any) => {
      if (!e.detail) return;

      const { inviteId } = e.detail;
      const storedData = window.sessionStorage.getItem('user');
      const data = storedData ? JSON.parse(storedData) : null;
      if (!data) return;

      socket.send(JSON.stringify({
        event: 'privateInviteResponse',
        inviteId: inviteId,
        accepted: true
      }));

      // usuń zaproszenie z kontekstu po inviteId
      setContext(prev => ({
        ...prev,
        privateWaitingUsers: (prev.privateWaitingUsers || []).filter(
          u => u.inviteId !== inviteId
        )
      }));
    }

    const handlePrivateCancel = (e: any) => {
      if (!e.detail) return;

      const { inviteId } = e.detail;
      const storedData = window.sessionStorage.getItem('user');
      const data = storedData ? JSON.parse(storedData) : null;
      if (!data) return;

      socket.send(JSON.stringify({
        event: 'privateInviteResponse',
        inviteId: inviteId,
        accepted: false
      }));

      // usuń zaproszenie z kontekstu po inviteId
      setContext(prev => ({
        ...prev,
        privateWaitingUsers: (prev.privateWaitingUsers || []).filter(
          u => u.inviteId !== inviteId
        )
      }));
    }

    const handleCloseRoom = (e: any) => {
      if (!e.detail) return;

      const { room } = e.detail as { type: string, room: IChatRoom };
      const storedData = window.sessionStorage.getItem('user');
      const user = storedData ? JSON.parse(storedData) : null;
      if (!user) return;

      socket.send(JSON.stringify({
        event: 'closeRoom',
        room: room,
        user: user,
      }));
    }

    const handleLogOut = (e: any) => {
      if (!e.detail) return;

      const { room } = e.detail as { type: string, room: IChatRoom };
      const storedData = window.sessionStorage.getItem('user');
      const user = storedData ? JSON.parse(storedData) : null;
      if (!user) return;

      socket.send(JSON.stringify({
        event: 'logOut',
        room: room,
        user: user,
      }));
    }

    const handleWebRTC = (e: any) => {
      const data = e.detail as IWebRTCData;
      if (!data) return;
      socket.send(JSON.stringify(data));
    };

    const handleRouletteNext = () => {
      socket.send(JSON.stringify({ event: 'rouletteNext' }));
    };

    const handleRouletteLeave = () => {
      socket.send(JSON.stringify({ event: 'rouletteLeave' }));
    };

    const handleRouletteFiltersUpdate = (e: any) => {
      const filters = (e?.detail || {}) as IRouletteFilters;
      socket.send(JSON.stringify({ event: 'rouletteFiltersUpdate', filters }));
    };

    const handleDeleteMessage = (e: any) => {
      const data = e.detail;
      if (!data) return;
      const storedData = window.sessionStorage.getItem('user');
      const user = storedData ? JSON.parse(storedData) : null;
      if (!user) return;

      socket.send(JSON.stringify({
        event: 'deleteMessage',
        user: user,
        room: data.room,
        message: data.message,
      }));
    };

    const handleHideMessage = (e: any) => {
      const data = e.detail;
      if (!data) return;
      const storedData = window.sessionStorage.getItem('user');
      const user = storedData ? JSON.parse(storedData) : null;
      if (!user) return;

      socket.send(JSON.stringify({
        event: 'hideMessage',
        user: user,
        room: data.room,
        message: data.message,
      }));
    };

    const handleBanWarning = (e: any) => {
      const data = e.detail;
      if (!data) return;
      const storedData = window.sessionStorage.getItem('user');
      const user = storedData ? JSON.parse(storedData) : null;
      if (!user) return;

      socket.send(JSON.stringify({
        event: 'banWarning',
        user: user,
        targetUserId: data.targetUserId,
      }));
    };

    const handleAddReaction = (e: any) => {
      const data = e.detail;
      if (!data) return;
      const storedData = window.sessionStorage.getItem('user');
      const user = storedData ? JSON.parse(storedData) : null;
      if (!user) return;

      socket.send(JSON.stringify({
        event: 'addReaction',
        user: user,
        room: { id: data.roomId },
        messageId: data.messageId,
        emojiId: data.emojiId,
      }));
    };

    const handleBanUser = (e: any) => {
      const data = e.detail;
      if (!data) return;
      const storedData = window.sessionStorage.getItem('user');
      const user = storedData ? JSON.parse(storedData) : null;
      if (!user) return;

      socket.send(JSON.stringify({
        event: 'banUser',
        user: user,
        targetUserId: data.targetUserId,
      }));
    };

    const handleKickUser = (e: any) => {
      const data = e.detail;
      if (!data) return;
      const storedData = window.sessionStorage.getItem('user');
      const user = storedData ? JSON.parse(storedData) : null;
      if (!user) return;

      socket.send(JSON.stringify({
        event: 'kickUser',
        user: user,
        targetUserId: data.targetUserId,
      }));
    };

    window.addEventListener('userMessage', handleUserMessage);
    window.addEventListener('changeRoom', handleChangeRoom);
    window.addEventListener('getPeople', handleGetPeople);
    window.addEventListener('getPeopleCount', handleGetPeopleCount);
    window.addEventListener('privateJoin', handlePrivateJoin);
    window.addEventListener('privateAccept', handlePrivateAccept);
    window.addEventListener('privateCancel', handlePrivateCancel);
    window.addEventListener('closeRoom', handleCloseRoom);
    window.addEventListener('logOut', handleLogOut);
    window.addEventListener('webRTC', handleWebRTC);
    window.addEventListener('callSessionAccept', handleWebRTC);
    window.addEventListener('rouletteNext', handleRouletteNext);
    window.addEventListener('rouletteLeave', handleRouletteLeave);
    window.addEventListener('rouletteFiltersUpdate', handleRouletteFiltersUpdate);

    // Eventy Moderatora
    window.addEventListener('deleteMessage', handleDeleteMessage);
    window.addEventListener('hideMessage', handleHideMessage);
    window.addEventListener('banWarning', handleBanWarning);
    window.addEventListener('kickUser', handleKickUser);
    window.addEventListener('banUser', handleBanUser);
    window.addEventListener('addReaction', handleAddReaction);

    // UWAGA: nie czyścimy listenerów ani nie zamykamy socketu w cleanup tego efektu —
    // socket żyje na poziomie App.tsx i przeżywa nawigację /chat <-> /roulette.
    // Pełne czyszczenie odbywa się w osobnym efekcie `[]` poniżej.
  }, [location.pathname]);

  // Twarde czyszczenie tylko przy realnym odmontowaniu App (reload / zamknięcie zakładki).
  useEffect(() => {
    return () => {
      if (websocketRef.current) {
        try { websocketRef.current.close(); } catch { /* ignore */ }
        websocketRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, []);

  return null;
};

export default Websocket;
